
import type { Client } from "@/types";
import { calculatePercentageChange } from "./metrics/percentageUtils";
import { 
  generateClientChartData, 
  generateProgramChartData, 
  generateNewClientsChartData, 
  generateRemovedClientsChartData 
} from "./metrics/chartDataGenerators";
import { getNewClientsCountWithComparison } from "./metrics/clientMetrics";
import { fetchRemovedClientsCount } from "./metrics/removedClientsMetrics";
import { fetchProgramCount } from "./metrics/programMetrics";

export const calculateMetrics = async (coachClients: Client[]) => {
  // Get actual program count
  const actualProgramCount = await fetchProgramCount();
  
  // Calculate all metrics
  const weeklyNewData = getNewClientsCountWithComparison(coachClients, 7);
  const monthlyNewData = getNewClientsCountWithComparison(coachClients, 30);
  const yearlyNewData = getNewClientsCountWithComparison(coachClients, 365);

  const weeklyRemovedData = await fetchRemovedClientsCount(7);
  const monthlyRemovedData = await fetchRemovedClientsCount(30);
  const yearlyRemovedData = await fetchRemovedClientsCount(365);

  return {
    totalClients: coachClients.length,
    activePrograms: actualProgramCount,
    newClients: {
      week: {
        value: weeklyNewData.currentCount,
        change: calculatePercentageChange(weeklyNewData.currentCount, weeklyNewData.previousCount),
        trend: weeklyNewData.currentCount >= weeklyNewData.previousCount ? "up" as const : "down" as const,
        chartData: generateNewClientsChartData(weeklyNewData.currentCount)
      },
      month: {
        value: monthlyNewData.currentCount,
        change: calculatePercentageChange(monthlyNewData.currentCount, monthlyNewData.previousCount),
        trend: monthlyNewData.currentCount >= monthlyNewData.previousCount ? "up" as const : "down" as const,
        chartData: generateNewClientsChartData(monthlyNewData.currentCount)
      },
      year: {
        value: yearlyNewData.currentCount,
        change: calculatePercentageChange(yearlyNewData.currentCount, yearlyNewData.previousCount),
        trend: yearlyNewData.currentCount >= yearlyNewData.previousCount ? "up" as const : "down" as const,
        chartData: generateNewClientsChartData(yearlyNewData.currentCount)
      }
    },
    removedClients: {
      week: {
        value: weeklyRemovedData.currentCount,
        change: calculatePercentageChange(weeklyRemovedData.currentCount, weeklyRemovedData.previousCount),
        trend: "down" as const,
        chartData: generateRemovedClientsChartData(weeklyRemovedData.currentCount)
      },
      month: {
        value: monthlyRemovedData.currentCount,
        change: calculatePercentageChange(monthlyRemovedData.currentCount, monthlyRemovedData.previousCount),
        trend: "down" as const,
        chartData: generateRemovedClientsChartData(monthlyRemovedData.currentCount)
      },
      year: {
        value: yearlyRemovedData.currentCount,
        change: calculatePercentageChange(yearlyRemovedData.currentCount, yearlyRemovedData.previousCount),
        trend: "down" as const,
        chartData: generateRemovedClientsChartData(yearlyRemovedData.currentCount)
      }
    },
    clientsChartData: generateClientChartData(coachClients.length),
    programsChartData: generateProgramChartData(actualProgramCount)
  };
};
